import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer, LEGAL_ENTITY } from '../components/Footer';
import { Shield, Users, Zap, Target, CheckCircle2 } from 'lucide-react';
import { OLLIE_IMAGES } from '../data/mock';

const AboutPage = () => {
  const scrollToCompare = () => {
    window.location.href = '/compare';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header onStartComparing={scrollToCompare} />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 to-orange-50/30 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About <span className="text-cyo-orange">CompareYourOptions.com.au</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're on a mission to help Australians make smarter energy decisions through 
              free, independent, and easy-to-understand comparisons.
            </p>
          </div>
        </section>

        {/* Who We Are */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Who We Are</h2>
                <p className="text-gray-600 mb-4">
                  {LEGAL_ENTITY.website} is owned and operated by <strong>{LEGAL_ENTITY.name}</strong> (ABN {LEGAL_ENTITY.abn}). 
                  We provide independent, easy-to-understand comparisons to help Australians make informed decisions 
                  about their home energy solutions.
                </p>
                <p className="text-gray-600 mb-4">
                  Our platform allows you to compare solar systems, battery storage, EV chargers, heat pumps, 
                  and heating & cooling solutions — all in one place, completely free of charge.
                </p>
                <p className="text-gray-600">
                  We believe that everyone deserves access to clear, unbiased information when making 
                  important decisions about their home energy needs.
                </p>
              </div>
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-64 h-64 bg-orange-100 rounded-full flex items-center justify-center">
                    <img 
                      src={OLLIE_IMAGES.waving} 
                      alt="Ollie the Owl - Our mascot" 
                      className="w-48 h-48 object-contain"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-white rounded-xl px-4 py-2 shadow-lg">
                    <p className="text-sm font-semibold text-gray-800">Meet Ollie!</p>
                    <p className="text-xs text-gray-500">Your comparison helper</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Our Values</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-cyo-orange" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Independence</h3>
                <p className="text-gray-600">
                  We provide unbiased comparisons with no hidden agendas. Our goal is to help you 
                  find the best option for your needs, not push a particular product.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-cyo-orange" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Customer First</h3>
                <p className="text-gray-600">
                  Everything we do is designed with you in mind. Our platform is free to use, 
                  easy to navigate, and focused on getting you the information you need.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-cyo-orange" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Simplicity</h3>
                <p className="text-gray-600">
                  Energy decisions shouldn't be complicated. We break down complex information 
                  into clear, understandable comparisons that help you decide with confidence.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-cyo-orange" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Transparency</h3>
                <p className="text-gray-600">
                  We're upfront about who we are and how we operate. No fine print, no hidden 
                  fees — just honest, straightforward comparisons.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">How We Help You</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-cyo-orange rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Choose Your Product</h3>
                  <p className="text-gray-600">
                    Select from solar systems, battery storage, EV chargers, heat pumps, or heating & cooling solutions.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-cyo-orange rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Tell Us About Your Needs</h3>
                  <p className="text-gray-600">
                    Answer a few quick questions about your location and energy usage so we can tailor our recommendations.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-cyo-orange rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">CompareYourOptions.com.au</h3>
                  <p className="text-gray-600">
                    Review personalised options and indicative pricing to find the best fit for your home and budget.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-cyo-orange rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Connect With Specialists</h3>
                  <p className="text-gray-600">
                    Our team will connect you with qualified specialists who can provide detailed quotes and installation services.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Important Information</h3>
              <p className="text-sm text-gray-600 mb-3">
                The information provided on {LEGAL_ENTITY.website} is for general comparison purposes only and 
                does not constitute financial, legal, or professional advice. All comparisons and pricing 
                information are indicative and may vary based on individual circumstances.
              </p>
              <p className="text-sm text-gray-600">
                We recommend consulting with qualified professionals before making any decisions regarding 
                your home energy solutions. {LEGAL_ENTITY.website} is owned and operated by {LEGAL_ENTITY.name} (ABN {LEGAL_ENTITY.abn}).
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Compare?</h2>
            <p className="text-gray-600 mb-8">
              Start your free comparison today and discover the best energy solutions for your home.
            </p>
            <Link 
              to="/compare"
              className="inline-flex items-center gap-2 bg-cyo-orange hover:bg-cyo-orange-dark text-white font-semibold px-8 py-4 rounded-xl shadow-lg transition-all duration-300"
            >
              <CheckCircle2 className="w-5 h-5" />
              Start Comparing Now
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
